import type { PostHog } from "posthog-js";

import { withoutCaptureTrailingSlash } from "@lib/telemetry/posthogCaptureUrl";
import { buildDeviceAuthHeaders, getClientBaseUrl } from "@client/trpc";
import { isTelemetryRequestFailure } from "@lib/telemetryFailure";

export { withoutCaptureTrailingSlash };

type SendRequestOptions = Parameters<PostHog["_send_request"]>[0];
type SendRequestResponse = Parameters<
  NonNullable<SendRequestOptions["callback"]>
>[0];

/** What we learn about one failed PostHog SDK request. */
export interface PostHogRequestFailure {
  /** Path of the request, query string dropped (e.g. `/ph/e/`). */
  path: string;
  /** HTTP status, or 0 when the browser never got a response (network error,
   * extension block, timeout abort). */
  statusCode: number;
  /** `error.name: message` for a status-0 failure, else null. */
  error: string | null;
  transport: string;
  compression: string;
  /** How many times the SDK had already retried this request. */
  retries: number;
}

const MAX_REPORTS_PER_PAGE = 10;
let reportedKeys = new Set<string>();

/** Test seam. */
export function resetRequestFailureReportingForTests(): void {
  reportedKeys = new Set();
}

function requestPath(url: string): string {
  try {
    return new URL(url, "https://invalid.local").pathname;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

function describeError(error: unknown): string | null {
  if (error == null) return null;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}

/**
 * Report PostHog SDK request failures, once per distinct failure per page.
 *
 * The SDK's own `on_request_error` fires only for HTTP 4xx/5xx. The failure
 * we need to see is the other kind: a capture POST that never gets a response
 * (status 0) because something in the user's browser rejects or hangs it. The
 * SDK swallows those into its retry queue, and after ten retries drops the
 * events silently. From the outside that reads as "this user does no
 * analytics", which is exactly how a funder's whole desktop usage went
 * missing — so the failure is reported, with the request shape (path,
 * transport, compression) that distinguishes the working requests from the
 * failing ones, to the Health ledger through our own server (reportToServer).
 *
 * Implemented by wrapping the SDK's request method: every attempt, including
 * retries and the page-hide beacons, funnels through it, and its callback
 * carries the status. Nothing else in the SDK observes status-0 outcomes.
 * The same seam applies withoutCaptureTrailingSlash to the request URL.
 */
export function installRequestFailureReporting(
  client: PostHog,
  report: (failure: PostHogRequestFailure) => void = reportToServer,
): void {
  if (typeof client._send_request !== "function") return;
  const send = client._send_request.bind(client);
  client._send_request = (options: SendRequestOptions) => {
    const retries =
      (options as { retriesPerformedSoFar?: number }).retriesPerformedSoFar ??
      0;
    const url = withoutCaptureTrailingSlash(options.url);
    send({
      ...options,
      url,
      callback: (response: SendRequestResponse) => {
        if (isTelemetryRequestFailure(response.statusCode)) {
          noteFailure(
            {
              path: requestPath(url),
              statusCode: response.statusCode,
              error: describeError(response.error),
              transport: options.transport ?? "fetch",
              compression: String(options.compression ?? "none"),
              retries,
            },
            report,
          );
        }
        options.callback?.(response);
      },
    });
  };
}

function noteFailure(
  failure: PostHogRequestFailure,
  report: (failure: PostHogRequestFailure) => void,
): void {
  const key = `${failure.path}|${failure.statusCode}|${failure.error ?? ""}`;
  if (reportedKeys.has(key) || reportedKeys.size >= MAX_REPORTS_PER_PAGE) {
    return;
  }
  reportedKeys.add(key);
  report(failure);
}

/**
 * The default send: a plain POST to the tRPC route with `keepalive`, so a
 * report fired from the page-hide beacon (one of the attempts this reporter
 * observes) survives the document unloading, where a fetch made through the
 * ordinary tRPC client would be cancelled. On the web the session cookie
 * carries the identity; the iPad app authenticates with a bearer token
 * against an absolute API host, and its users are exactly who this signal
 * exists for, so the device auth headers ride along. Deliberately the
 * device-scoped set, without the acting-as header: the route attributes the
 * failure to the signed-in person whoever they are acting as. The route
 * takes a plain JSON body.
 */
export function sendTelemetryFailure(
  failure: PostHogRequestFailure,
): Promise<unknown> {
  return fetch(`${getClientBaseUrl()}/api/trpc/reportTelemetryFailure`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      ...buildDeviceAuthHeaders(),
    },
    body: JSON.stringify(failure),
  });
}

/**
 * Default report target: the Health ledger, through our own server, as one
 * row per person. Nothing else can carry it — PostHog is what is failing,
 * and the Sentry issue this used to raise became a Slack ping per person.
 * If our server is unreachable too there is nothing left to tell, so a
 * failed report is dropped without a trace.
 */
export function reportToServer(
  failure: PostHogRequestFailure,
  send: (
    failure: PostHogRequestFailure,
  ) => Promise<unknown> = sendTelemetryFailure,
): void {
  void send(failure).catch(() => {});
}
